import { useIntl } from "react-intl";
import { Box, Paper, Typography } from "@mui/material";

import { getModelLabel } from "./constants";
import { MLMetrics } from "./services";

const ConfusionMatrixGrid = ({
  cm,
  modelLabel,
}: {
  cm: [number, number, number, number];
  modelLabel: string;
}) => {
  const intl = useIntl();
  const [tn, fp, fn, tp] = cm;
  const total = tn + fp + fn + tp;
  const cell = (
    value: number,
    label: string,
    sublabel: string,
    color: string
  ) => (
    <Box
      sx={{
        flex: 1,
        textAlign: "center",
        p: 1.5,
        backgroundColor: color,
        borderRadius: 1,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
        }}
      >
        {intl.formatNumber(value)}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          display: "block",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        {sublabel}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
        }}
      >
        {total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "—"}
      </Typography>
    </Box>
  );
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          mb: 1.5,
        }}
      >
        Modèle : <strong>{modelLabel}</strong>
      </Typography>
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
          <Box sx={{ flex: 1 }} />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              flex: 1,
              textAlign: "center",
            }}
          >
            Prédit : Confirmé
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              flex: 1,
              textAlign: "center",
            }}
          >
            Prédit : Annulé
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, mb: 0.5, alignItems: "center" }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              flex: 1,
              textAlign: "right",
              pr: 1,
            }}
          >
            Réel : Confirmé
          </Typography>
          {cell(
            tn,
            "Vrai Négatif ✓",
            "Bien prédit",
            "rgba(33, 241, 243, 0.20)"
          )}
          {cell(
            fp,
            "Faux Positif ⚠",
            "Fausse alerte",
            "rgba(246, 146, 0, 0.20)"
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              flex: 1,
              textAlign: "right",
              pr: 1,
            }}
          >
            Réel : Annulé
          </Typography>
          {cell(
            fn,
            "Faux Négatif ✗",
            "Annulation manquée",
            "rgba(255, 88, 93, 0.22)"
          )}
          {cell(
            tp,
            "Vrai Positif ✓",
            "Bien détectée",
            "rgba(0, 204, 204, 0.22)"
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ConfusionMatrix = ({ model }: { model: MLMetrics }) => (
  <Paper elevation={1} sx={{ p: 2, flex: "1 1 300px" }}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        mb: 1,
      }}
    >
      Matrice de confusion
    </Typography>
    {model.confusion_matrix.length === 4 ? (
      <ConfusionMatrixGrid
        cm={model.confusion_matrix as [number, number, number, number]}
        modelLabel={getModelLabel(model.model_name)}
      />
    ) : (
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        Données non disponibles — relancez l'entraînement.
      </Typography>
    )}
  </Paper>
);

export default ConfusionMatrix;
